import { useCallback, useMemo, useState } from 'react'

import { plataCentavos, plataTipeada } from '@/lib/formato'
import { confirmar } from '@/lib/confirmar'
import { Ulid } from '@/lib/ulid'
import type { CobroDirecto, MedioPagoPos } from './tipos'
import { medioPorDefecto } from './medioPorDefecto'
import { useTecladoNumerico } from './useTecladoNumerico'
import { soltarElFoco } from './useTeclaAgregar'
import { borrarUno, centavosTipeados, tipear } from './montoTipeado'
import { useTeclaDelMedio } from './useTeclaDelMedio'

// Las REGLAS del cobro sin detalle, en un solo lugar.
//
// Hay dos pantallas que cobran así: el diálogo que se abre con F3 en el POS
// normal, y la pantalla ENTERA del local que no carga productos (§1.1,
// `pos.solo_cobro`). Las dos hacen exactamente la misma operación y por eso
// comparten esto en lugar de tener cada una su copia: el pago implícito, la
// referencia que traba el cobro, el vuelto y el texto de la confirmación son
// decisiones, y dos copias de una decisión es una que el día que cambie va a
// cambiar en un lado nada más. Es el mismo criterio de `medioPorDefecto` y de
// `redondeoEfectivo`, y el gotcha que ya se pagó con los atajos de rango de
// fechas —el listado de ventas tenía su propia copia y arreglar los reportes
// no lo arregló—.
//
// Lo que queda AFUERA es la presentación: cada una arma su layout con sus
// tamaños, porque un diálogo entre dos clientes y una pantalla que se mira ocho
// horas no se ven igual.

export interface PagoCargado {
  medio: MedioPagoPos
  importeCentavos: number
  referencia?: string
}

/**
 * Cuánto agrega el botón del cobro dividido, y por qué no puede.
 *
 * Está afuera del hook y es PURA para poder testearla: es una regla de plata
 * —decide el importe de un pago— y las dos formas de errarle son caras. Cargar
 * más de lo que falta arma un cobro que no cierra contra el total y el servidor
 * lo rechaza con el cliente adelante; y cargar el total entero en el primer
 * toque, que es lo que hacía antes, deja la división sin nada para el segundo
 * medio.
 *
 * El campo VACÍO significa «todo lo que falta»: es lo que hace que el último
 * pago sea un solo toque en lugar de tipear el resto a mano, que es justo la
 * resta que el sistema tiene que hacer por el cajero.
 */
export function reglaDeAgregar({
  medio,
  montoCentavos,
  cobradoCentavos,
  pagoCentavos,
  referencia,
}: {
  medio: MedioPagoPos | null
  montoCentavos: number
  cobradoCentavos: number
  pagoCentavos: number
  referencia: string
}): { importe: number; motivo: string | null } {
  const falta = Math.max(0, montoCentavos - cobradoCentavos)
  const importe = pagoCentavos > 0 ? pagoCentavos : falta

  const motivo =
    medio === null
      ? 'Elegí con qué medio entra este pago.'
      : montoCentavos === 0
        ? 'Poné primero el total del ticket.'
        : falta === 0
          ? 'Ya está cubierto todo el total.'
          : medio.pideReferencia && referencia.trim().length === 0
            ? `${medio.nombre} pide el número de comprobante.`
            : importe > falta
              ? `No puede ser más de lo que falta (${plataCentavos(falta)}).`
              : null

  return { importe, motivo }
}

export function useCobroSinDetalle({
  medios,
  onCobrar,
  cobrando,
}: {
  medios: MedioPagoPos[]

  /**
   * Registra el cobro. Devuelve si quedó registrado, y eso importa: la
   * pantalla completa NO se desmonta después de cobrar, así que necesita saber
   * si puede limpiarse. Limpiar cuando falló perdería el monto tipeado y
   * —peor— renovaría la clave de idempotencia, así que el reintento entraría
   * como un cobro NUEVO y la plata se registraría dos veces.
   */
  onCobrar: (datos: CobroDirecto) => void | Promise<boolean | void>

  cobrando: boolean
}) {
  // El ULID se genera UNA vez y no al mandar: es la clave de idempotencia y
  // generarla en el envío la hace decorativa. Si el pedido se corta después de
  // que el servidor guardó, el cajero vuelve a apretar Cobrar, va el mismo id y
  // vuelve el mismo cobro en lugar de un segundo. La flecha va explícita porque
  // `useState(Ulid.nuevo)` lo rechaza el linter (unbound-method).
  const [ulid, setUlid] = useState(() => Ulid.nuevo())

  // El monto se tipea con su coma y se guarda en centavos. Qué tecla entra lo
  // decide `montoTipeado`, con tests.
  const [digitos, setDigitos] = useState('')
  const [comprobante, setComprobante] = useState('')
  const [referencia, setReferencia] = useState('')
  const [pagos, setPagos] = useState<PagoCargado[]>([])

  /**
   * En qué momento del cobro estamos, y es la corrección de una pantalla que
   * no se entendía.
   *
   * Antes había un solo teclado y significaba dos cosas a la vez: el TOTAL del
   * ticket y, cuando se dividía el cobro, el importe de cada pago. Con eso
   * pasaban las dos cosas que reportó el mostrador:
   *
   *  - «Dividir» cargaba SIEMPRE lo que faltaba, o sea el total entero en el
   *    primer toque, así que nunca quedaba nada para el segundo medio.
   *  - Tipear después cambiaba el TOTAL —no el importe del pago—, así que
   *    poner un número más chico que lo ya cargado dejaba la pantalla en un
   *    estado imposible: total $300 con $2.500 en pagos, sin ningún botón que
   *    lo arregle.
   *
   * Ahora son dos momentos con una sola lectura cada uno: en `total` el
   * teclado es el total del ticket; en `dividido` el total queda FIJO y el
   * teclado es el importe del pago que se está cargando. La pantalla dice cuál
   * de los dos está editando, y se vuelve atrás con `volverAUnMedio`, que
   * limpia los pagos: sin salida de vuelta, cualquier error de tipeo obliga a
   * cerrar y empezar de nuevo.
   */
  const [modo, setModo] = useState<'total' | 'dividido'>('total')

  /** El importe del pago que se está cargando. Solo se usa en `dividido`. */
  const [digitosPago, setDigitosPago] = useState('')

  // Con cuánto paga, como TEXTO: es un campo que se tipea y admite centavos.
  // `null` significa «nadie lo tocó», que es lo que hace que siga al monto.
  const [entregadoTexto, setEntregado] = useState<string | null>(null)

  // Mientras la confirmación está arriba, los dígitos NO tienen que entrar: si
  // no, tipear con el diálogo abierto cambia el monto que se está por
  // confirmar y lo que se registra no es lo que la persona leyó.
  const [confirmando, setConfirmando] = useState(false)

  // El medio: `null` significa «nadie eligió» y ahí vale el del local.
  //
  // Se DERIVA en el render en lugar de inicializar el estado con el default, y
  // eso resuelve dos cosas de una. Una: la pantalla completa se monta antes de
  // que la consulta de medios responda, así que un valor inicial calculado una
  // sola vez quedaría en null para siempre y el cajero tendría que elegir el
  // medio en cada venta del día. Dos: después de cobrar, volver a null hace que
  // el cobro siguiente arranque otra vez en el medio preseleccionado del local,
  // que es lo que corresponde —el QR de la venta anterior no es el default de
  // la próxima—.
  const [medioElegido, setMedio] = useState<MedioPagoPos | null>(null)
  const medio = medioElegido ?? medioPorDefecto(medios)

  // El teclado escribe en el buffer que está activo, y va con `useCallback`
  // porque el hook del teclado físico se re-suscribe cuando cambia: una flecha
  // nueva en cada render sería un listener nuevo en cada tecla.
  const escribir = useCallback(
    (f: (v: string) => string) => {
      if (modo === 'dividido') setDigitosPago(f)
      else setDigitos(f)
    },
    [modo],
  )

  // El teclado de la MÁQUINA, además del de la pantalla: en una caja con
  // teclado, cargar el monto con el mouse es absurdo. Ver el hook.
  useTecladoNumerico(escribir, { activo: !confirmando, agregar: tipear })

  // La tecla rápida de cada medio, enganchada de verdad.
  useTeclaDelMedio(medios, setMedio)

  const montoCentavos = centavosTipeados(digitos)

  // El fiado NO entra acá, y es una decisión, no un olvido. Fiar es anotarle
  // una deuda a un vecino, y para eso hay que saber qué se llevó: una cuenta
  // corriente con renglones que dicen «cobro sin detalle» es una cuenta que no
  // se puede discutir con el cliente cuando venga a pagar.
  const cobrables = useMemo(() => medios.filter((m) => !m.esCuentaCorriente), [medios])

  const cobrado = useMemo(() => pagos.reduce((a, p) => a + p.importeCentavos, 0), [pagos])
  const falta = Math.max(0, montoCentavos - cobrado)

  // El medio elegido pide referencia y no está escrita: es lo único que puede
  // trabar el cobro con el monto y el medio ya puestos, y por eso la pantalla
  // lo tiene que DECIR —el botón deshabilitado en silencio se ve como un
  // sistema que no anda—.
  const faltaReferencia =
    medio?.pideReferencia === true && referencia.trim().length === 0 && montoCentavos > 0

  const pagoCentavos = centavosTipeados(digitosPago)

  // La regla vive afuera y con tests: decide el importe de un pago, y ese es
  // un número que después el servidor compara contra el total.
  const { importe: importeAAgregar, motivo: motivoNoAgregar } = reglaDeAgregar({
    medio,
    montoCentavos,
    cobradoCentavos: cobrado,
    pagoCentavos,
    referencia,
  })

  const puedeAgregar = motivoNoAgregar === null

  const agregar = () => {
    if (!puedeAgregar || !medio) return

    // Entrar en modo dividido al agregar el PRIMER pago, y no antes: así el
    // botón «Dividir el cobro» y el de agregar son el mismo camino y no hay
    // dos formas de llegar al mismo estado.
    setModo('dividido')

    setPagos((v) => [
      ...v,
      {
        medio,
        importeCentavos: importeAAgregar,
        referencia: medio.pideReferencia ? referencia.trim() : undefined,
      },
    ])

    setReferencia('')
    setDigitosPago('')

    // Por lo mismo que en el diálogo de cobro del POS: el foco del botón que
    // agregó el pago se suelta, así el Enter siguiente cobra.
    soltarElFoco()
  }

  /**
   * Empezar a dividir: el total queda fijo y el teclado pasa a ser el importe
   * del pago.
   */
  const dividir = () => {
    if (montoCentavos === 0) return
    setModo('dividido')
    setDigitosPago('')
  }

  /** Volver a un solo medio. Limpia los pagos: es la salida de la división. */
  const volverAUnMedio = () => {
    setModo('total')
    setPagos([])
    setDigitosPago('')
    setReferencia('')
  }

  const quitar = (indice: number) => setPagos((v) => v.filter((_, i) => i !== indice))

  // El pago que se da por entendido cuando hay UN solo medio.
  //
  // Es la agilidad del mostrador: en una verdulería casi todo se cobra con un
  // medio y de una vez, así que exigir «Agregar el pago» para después apretar
  // «Cobrar» es una tecla de más en la operación más frecuente del día. Cargar
  // el pago a mano queda para lo que de verdad lo necesita: dividir el cobro
  // entre dos medios.
  //
  // El medio tiene que estar en condiciones: si pide referencia, la referencia
  // tiene que estar escrita, o se registraría un cobro por transferencia sin el
  // número con el que después se concilia.
  const implicito: PagoCargado | null =
    // Dividiendo NO hay pago implícito: si lo hubiera, apretar Cobrar recién
    // entrado al modo registraría el total entero con el medio elegido, que es
    // lo contrario de lo que la persona acaba de pedir.
    modo === 'total' &&
    pagos.length === 0 &&
    medio !== null &&
    montoCentavos > 0 &&
    (!medio.pideReferencia || referencia.trim().length > 0)
      ? {
          medio,
          importeCentavos: montoCentavos,
          referencia: medio.pideReferencia ? referencia.trim() : undefined,
        }
      : null

  const aCobrar = implicito ? [implicito] : pagos

  // Lo que falta DESPUÉS de contar el pago implícito. Es lo que se muestra: con
  // un solo medio elegido no falta nada, y el cartel rojo diciendo «FALTA
  // $3.500» al lado de un botón habilitado que registra $3.500 es la pantalla
  // contradiciéndose.
  const faltaVisible = Math.max(
    0,
    montoCentavos - aCobrar.reduce((a, p) => a + p.importeCentavos, 0),
  )

  const efectivo = aCobrar.length > 0 && aCobrar.every((p) => p.medio.daVuelto)

  // Vacío significa JUSTO —borrar el campo no es «paga cero»— y el campo lo
  // muestra precargado con el monto, porque pagar el importe exacto es el caso
  // más común y arrancar en blanco obliga a tipear el mismo número dos veces.
  const entregado =
    entregadoTexto === null || entregadoTexto === ''
      ? montoCentavos
      : centavosTipeados(entregadoTexto)

  // Solo cuando hay algo que devolver. Con el campo precargado en «paga justo»,
  // el cartel verde y grande decía «VUELTO $0,00» en cada cobro, que se lee
  // como un resultado que hay que entregar.
  const vuelto = efectivo && entregado > montoCentavos ? entregado - montoCentavos : null

  const listo =
    montoCentavos > 0 && aCobrar.reduce((a, p) => a + p.importeCentavos, 0) === montoCentavos

  /**
   * Deja todo listo para el cliente siguiente, con una clave de idempotencia
   * NUEVA.
   *
   * Las dos mitades tienen que valer, igual que en el ticket del POS: la clave
   * pegada a la operación para que el reintento sea el MISMO cobro, y renovada
   * al limpiar para que el cobro siguiente no devuelva el anterior. Se llama
   * solo cuando el cobro quedó registrado.
   */
  const reiniciar = useCallback(() => {
    setDigitos('')
    setDigitosPago('')
    setModo('total')
    setComprobante('')
    setReferencia('')
    setPagos([])
    setEntregado(null)
    setMedio(null)
    setUlid(Ulid.nuevo())
  }, [])

  const cobrar = async (): Promise<boolean> => {
    if (!listo || cobrando || confirmando) return false

    // Confirmación con los datos del cobro a la vista, y en DOS teclas: Enter
    // acá y Enter en el diálogo. Es el único control que tiene esta operación
    // —no hay renglones que revisar, solo un monto que alguien tipeó— y un
    // dígito de más en el importe es plata que entra mal al arqueo y que
    // después hay que anular.
    setConfirmando(true)

    const detalle = [
      `Medio: ${aCobrar.map((p) => p.medio.nombre).join(' + ')}.`,
      efectivo && entregado > montoCentavos
        ? `Paga con ${plataCentavos(entregado)} y el vuelto es ${plataCentavos(entregado - montoCentavos)}.`
        : efectivo
          ? 'Paga justo, sin vuelto.'
          : null,
      comprobante.trim() !== '' ? `Comprobante ${comprobante.trim()}.` : null,
    ]
      .filter(Boolean)
      .join(' ')

    const si = await confirmar({
      titulo: `¿Registrar ${plataCentavos(montoCentavos)}?`,
      detalle,
      confirmar: 'Confirmar el cobro',
      enfocarConfirmar: true,
    })

    setConfirmando(false)

    if (!si) return false

    const resultado = await onCobrar({
      ulid,
      montoCentavos,
      comprobanteExterno: comprobante.trim() === '' ? undefined : comprobante.trim(),
      entregadoCentavos: efectivo ? entregado : undefined,
      pagos: aCobrar.map((p) => ({
        medioPagoIdNube: p.medio.id,
        importeCentavos: p.importeCentavos,
        referencia: p.referencia,
      })),
    })

    // `void` significa «no sé si salió» y se toma como que SÍ: es el caso del
    // diálogo, que se desmonta y no tiene nada que limpiar. Solo un `false`
    // explícito —el que devuelve la pantalla completa cuando el servidor
    // rechazó— conserva lo tipeado y la clave.
    return resultado !== false
  }

  return {
    // En qué momento estamos y cómo moverse entre los dos
    modo,
    dividir,
    volverAUnMedio,

    // El monto
    digitos,
    montoCentavos,
    montoTexto: plataTipeada(digitos),

    // El importe del pago que se está cargando (solo en modo dividido)
    pagoCentavos,
    pagoTexto: plataTipeada(digitosPago),

    // Lo que el teclado está editando AHORA, con su etiqueta: es lo único que
    // la pantalla tiene que mostrar, y así no puede mostrar una cosa y editar
    // la otra.
    tecleandoTexto: modo === 'dividido' ? plataTipeada(digitosPago) : plataTipeada(digitos),
    tecleandoEtiqueta: modo === 'dividido' ? 'Importe de este pago' : 'Monto del ticket',

    tecla: {
      digito: (d: string) => escribir((v) => tipear(v, d)),
      coma: () => escribir((v) => tipear(v, ',')),
      borrar: () => escribir(borrarUno),
      limpiar: () => escribir(() => ''),
    },

    // Los medios
    cobrables,
    medio,
    elegirMedio: setMedio,
    referencia,
    setReferencia,
    faltaReferencia,

    // El comprobante del otro equipo
    comprobante,
    setComprobante,

    // El cobro dividido
    pagos,
    agregar,
    quitar,
    puedeAgregar,
    motivoNoAgregar,
    importeAAgregar,
    falta,
    faltaVisible,
    implicito,

    // Efectivo
    efectivo,
    entregadoTexto,
    entregado,
    setEntregado,
    vuelto,

    // La acción
    listo,
    confirmando,
    cobrar,
    reiniciar,
  }
}
